import { useCallback, useEffect, useState } from "react";
import { dialogTitleStyle } from "./destructiveStyles";

interface ShareableLinkDialogProps {
  link: string;
  onClose: () => void;
}

export default function ShareableLinkDialog({ link, onClose }: ShareableLinkDialogProps) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  useEffect(() => {
    if (copyState === "idle") return;
    const timer = setTimeout(() => setCopyState("idle"), 2000);
    return () => clearTimeout(timer);
  }, [copyState]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
  };

  return (
    <div
      style={overlayStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="false"
      data-app-usage-surface="sharing"
    >
      <div style={dialogStyle} onClick={(event) => event.stopPropagation()}>
        <h2 style={dialogTitleStyle}>Shareable link</h2>
        <div style={rowStyle}>
          <input readOnly aria-label="Shareable link" value={link} style={inputStyle} />
          <button type="button" onClick={() => void handleCopy()} style={primaryButtonStyle}>
            {copyState === "copied" ? "Copied!" : copyState === "error" ? "Retry" : "Copy link"}
          </button>
        </div>
        <p style={noteStyle} data-testid="shareable-link-note">
          The exported copy of your drawing is stored in this app&apos;s protected share storage,
          and access is governed by your organization&apos;s controls. Only share this link with
          people who should have access.
        </p>
      </div>
    </div>
  );
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  background: "var(--exms-overlay-bg)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const dialogStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  boxSizing: "border-box",
  background: "var(--island-bg-color)",
  borderRadius: 12,
  border: "1px solid var(--dialog-border-color)",
  boxShadow: "var(--exms-shadow-modal)",
  padding: 24,
  fontFamily:
    "Assistant, system-ui, BlinkMacSystemFont, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
};

const rowStyle: React.CSSProperties = {
  marginTop: 20,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const inputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid var(--dialog-border-color)",
  background: "var(--input-bg-color)",
  color: "var(--text-primary-color)",
  fontSize: 15,
};

const primaryButtonStyle: React.CSSProperties = {
  alignSelf: "flex-end",
  padding: "12px 20px",
  border: "1px solid var(--color-primary)",
  borderRadius: 10,
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
};

const noteStyle: React.CSSProperties = {
  marginTop: 18,
  marginBottom: 0,
  paddingTop: 16,
  borderTop: "1px solid var(--dialog-border-color)",
  color: "var(--color-text-secondary)",
  fontSize: 14,
  lineHeight: 1.5,
};
